import * as styles from './EmailForm.module.css'
import React, { useState, useEffect } from 'react'
import { device } from '../../utils'
import { InputGroup, Button, Text } from '../../components/Core'
import { useTranslation } from 'react-i18next'
import 'isomorphic-fetch'
import { useSelector } from 'react-redux'
import theme from '../../utils/theme'
import styled from 'styled-components'

interface RootState {
  user: {
    user_settings: {
      dark_mode: boolean
    }
    session_expired: boolean
    darkMode: boolean
  }
}

interface EmailFormProps {
  customEmail?: boolean
  btnText?: string
  formType?: 'Hero' | 'mailLite'
  [key: string]: any
}

declare global {
  interface Window {
    _hsq: any[]
  }
  namespace NodeJS {
    interface ProcessEnv {
      CGY_SIGNUP: string
    }
  }
}

const MailLiteForm = styled.div<{ userDarkModeSelector: boolean }>`
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-embedBody .ml-form-horizontalRow button {
    background-color: #3377C6 !important;
    width: 132px;
    height: 44px;
    border-radius: 6px;
    border-color: #3377C6;
    margin-top: 5px !important;
    margin-left: 150px !important;
    font-style: normal;
    font-weight: 400;
    font-size: 16px !important;
    line-height: 20px;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-embedBody .ml-form-horizontalRow button:hover {
    background-color: #333!important;
    border-color: #333!important;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-embedBody {
    padding: 0px;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper {
    background-color: transparent;
    margin-left: 0px;
    align-items: left;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-align-center {
    text-align: start;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-embedBody .ml-form-horizontalRow input {
    width: 570px !important;
    max-width: 100vw !important;
    height: 54px;
    background-color: ${
      (props: { userDarkModeSelector: boolean }) =>
        props.userDarkModeSelector
          ? '#191B20 !important'
          : '#F0F0F0 !important'
    };
    border-radius: 6px !important;
    border-color: transparent !important;
    font-style: normal;
    font-weight: 400;
    font-size: 18px !important;
    line-height: 20px;
    overflow: hidden;
    padding: 15px 15px 8px 15px;
    box-shadow: 0 42px 54px rgba(65, 65, 101, 0.16);
  }
  .ml-form-formContent.horozintalForm .ml-form-horizontalRow .ml-input-horizontal {
    width: 70%;
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-successBody .ml-form-successContent h4 {
    color: ${
      (props: { userDarkModeSelector: boolean }) =>
        props.userDarkModeSelector ? '#FAFAFA !important' : 'black !important'
    };
  }
  #mlb2-4573645.ml-form-embedContainer .ml-form-embedWrapper .ml-form-successBody .ml-form-successContent p {
    color: ${
      (props: { userDarkModeSelector: boolean }) =>
        props.userDarkModeSelector ? '#FAFAFA !important' : 'black !important'
    };
  }
  .ml-form-formContent button.primary, .ml-mobileButton-horizontal button.primary {
    text-indent: -9999px;
    line-height: 0;
  }
  .ml-form-formContent button.primary:after, .ml-mobileButton-horizontal button.primary:after {
    text-indent: initial;
    line-height: initial;
    display: block;
    content: 'Notify me';
    top: -20px;
    position: relative;
  }
  @media${device.xs} {
    #mlb2-4573645 .ml-mobileButton-horizontal button {
      background-color: #3377C6 !important;
      width: 100% !important;
      height: 44px;
      border-radius: 6px;
      border-color: #3377C6;
      margin-top: 5px !important;
      font-style: normal;
      font-weight: 400;
      font-size: 16px !important;
      line-height: 20px;
    }
  }
  @media${device.sm} {
    #mlb2-4573645  {
      padding-right: 9rem;
    }
  }
`

const SignupButton = styled(Button)`
  margin-top: 1rem;
  @media${device.sm} {
    margin-top: 0;
    position: absolute;
    top: 50%;
    right: 1.5rem;
    transform: translateY(-50%);
  }
`

const EmailForm: React.FC<EmailFormProps> = ({
  customEmail,
  btnText,
  ...props
}) => {
  const { t } = useTranslation()
  const userSettings = useSelector((state: RootState) => state.user.user_settings)
  const userSessionExpired = useSelector((state: RootState) => state.user.session_expired)
  const userDarkModeSelector = useSelector((state: RootState) => state.user.darkMode)
  const userDarkMode = userSessionExpired
    ? userDarkModeSelector
    : typeof userSettings !== 'undefined'
    ? userSettings.dark_mode
    : false

  // Email Input Style : Light.
  const inputLight = {
    color: theme.colors.darkGray,
    background: theme.colors.light,
    placeholder: theme.colors.mediumGray,
  }

  // Email Input Style : Dark.
  const inputDark = {
    color: theme.colors.light,
    background: theme.colors.darkGray,
    placeholder: theme.colors.lightGray,
  }

  const inputColorsTheme = userDarkMode ? inputDark : inputLight

  // Custom Config.
  const inputColor = customEmail ? inputColorsTheme.color : theme.colors.light
  const inputBackground = customEmail
    ? inputColorsTheme.background
    : theme.colors.primary
  const inputPlaceHolder = customEmail
    ? inputColorsTheme.placeholder
    : theme.colors.lighter
  const inputBorder = customEmail && theme.colors.lighter
  const btnContent = btnText ? btnText : t('landing.getStarted')

  const [emailValue, setEmailValue] = useState<string>('')
  const hubspot_identify = () => {
    const _hsq = (window._hsq = window._hsq || [])
    _hsq.push(['identify', { email: emailValue }])
    _hsq.push(['trackPageView'])
  }

  useEffect(() => {
    localStorage.setItem('cgyEmail', emailValue)
  }, [emailValue])

  const handleSubmit = () => {
    hubspot_identify()
    window.open(`${process.env.CGY_SIGNUP}`, '_blank')
  }

  if (props.formType === 'Hero') {
    return (
      <div>
        <div className="position-relative">
          <InputGroup
            type="email"
            name="f312ee3b63be1a5aa1db80fe0d7dcad8"
            icon={
              <i
                style={{ color: inputPlaceHolder }}
                className="icon icon-email-84 mt-1"
              />
            }
            placeholder={t('landing.yourEmail')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailValue(e.target.value)}
            required
            border="none"
            borderRadius={6}
            css={`
              background-color: ${inputBackground};
              box-shadow: 0 42px 54px rgba(0, 0, 0, 0.16);
              color: ${inputColor};
              padding-top: 0.9rem;
              padding-bottom: 1rem;
              ::placeholder {
                color: ${inputPlaceHolder};
              }
              @media${device.sm} {
                padding-right: 160px;
              }
            `}
          />
          <SignupButton
            type="submit"
            onClick={() => handleSubmit()}
            style={{
              fontSize: '16px',
              paddingTop: '0.65rem',
              paddingBottom: '0.7rem',
            }}
            background={'#124D95'}
          >
            {btnContent}
          </SignupButton>
        </div>
      </div>
    )
  } else if (props.formType === 'mailLite') {
    return (
      <MailLiteForm
        className="ml-form-embed"
        data-account="1887272:q6x0d1k8b7"
        data-form="4573645:s9a7v2"
        userDarkModeSelector={userDarkModeSelector}
      />
    )
  } else {
    return (
      <div>
        <InputGroup
          type="email"
          name="f312ee3b63be1a5aa1db80fe0d7dcad8"
          icon={
            <i
              style={{ color: inputPlaceHolder }}
              className="icon icon-email-84"
            />
          }
          placeholder={t('landing.yourEmail')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmailValue(e.target.value)}
          required
          border="none"
          css={`
            background-color: ${inputBackground};
            box-shadow: 0 42px 54px rgba(0, 0, 0, 0.16);
            color: ${inputColor};
            ::placeholder {
              color: ${inputPlaceHolder};
            }
            border: 1px solid ${inputBorder};
            @media${device.sm} {
              padding-right: 210px;
            }
          `}
        />
        <SignupButton
          type="submit"
          onClick={() => handleSubmit()}
          style={{ fontSize: '16px' }}
          background={'#124D95'}
        >
          {btnContent}
        </SignupButton>
      </div>
    )
  }
}

export default EmailForm
